import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  FileDown,
} from 'lucide-react';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { Card } from '@/components/Card/Card';
import { Table } from '@/components/Table/Table';
import {
  fetchCurvaAbcReport,
  relatorioCurvaAbcKeys,
} from '@/services/relatorioCurvaAbcService';
import type {
  IRelatorioCurvaAbcFiltros,
  IRelatorioCurvaAbcResponse,
  IRelatorioCurvaAbcSearch,
  TRelatorioCurvaAbcClassificacao,
} from '@/types/relatorioCurvaAbc';
import { RelatorioCurvaAbcForm } from './form';
import './curva-abc.css';

export const Route = createFileRoute('/_authenticated/relatorio/curva-abc/')({
  component: RelatorioCurvaAbcPage,
});

const INITIAL_FILTROS: IRelatorioCurvaAbcFiltros = {
  dataInicio: '',
  dataFim: '',
  tipoMovimentacao: 'saida',
};

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const EMPTY_ROWS: IRelatorioCurvaAbcResponse['dados'] = [];

const CLASS_META: Record<
  TRelatorioCurvaAbcClassificacao,
  {
    valorPct: number;
    label: string;
    Icon: typeof ArrowUpRight;
    color: string;
  }
> = {
  A: { valorPct: 80, label: 'A', Icon: ArrowUpRight, color: '#b91c1c' },
  B: { valorPct: 15, label: 'B', Icon: ArrowRight, color: '#c2410c' },
  C: { valorPct: 5, label: 'C', Icon: ArrowDownRight, color: '#15803d' },
};

const CLASS_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  datasets: {
    bar: {
      barPercentage: 0.6,
      categoryPercentage: 0.6,
      borderRadius: 4,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        display: true,
      },
      ticks: {
        precision: 0,
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
};

function buildClassChartData(classCounts: Record<TRelatorioCurvaAbcClassificacao, number>) {
  const classes: TRelatorioCurvaAbcClassificacao[] = ['A', 'B', 'C'];
  return {
    labels: classes.map((classe) => `Classe ${classe}`),
    datasets: [
      {
        label: 'Produtos',
        data: classes.map((classe) => classCounts[classe]),
        backgroundColor: classes.map((classe) => CLASS_META[classe].color),
      },
    ],
  };
}

function formatarData(data: string): string {
  if (!data) return '';
  return new Date(data).toLocaleDateString('pt-BR');
}

function formatarPeriodoCurto(inicio: string, fim: string): string {
  const ini = formatarData(inicio);
  const end = formatarData(fim);
  if (!ini || !end) return '—';
  return `${ini.slice(0, 5)} - ${end.slice(0, 5)}`;
}

function formatarMoeda(valor: number): string {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatarPercentual(valor: number): string {
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function formatarPosicao(index: number): string {
  return String(index + 1).padStart(3, '0');
}

function getPaginationPages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

function exportarExcel(dados: IRelatorioCurvaAbcResponse, filtros: IRelatorioCurvaAbcSearch): void {
  if (!dados.dados.length) return;

  const wb = XLSX.utils.book_new();

  const dadosRelatorio = dados.dados.map((item, index) => ({
    Posição: index + 1,
    Código: item.codigo_produto,
    Descrição: item.descricao,
    [dados.tipo_movimentacao === 'entrada' ? 'Entrada (Período)' : 'Venda (Período)']:
      item.movimentacao_periodo,
    'Saldo em Estoque': item.saldo_estoque,
    'Percentual (%)': item.percentual,
    'Percentual Acumulado (%)': item.percentual_acumulado,
    'Classificação ABC': item.classificacao,
  }));

  const ws = XLSX.utils.json_to_sheet(dadosRelatorio);
  ws['!cols'] = [
    { wch: 8 },
    { wch: 15 },
    { wch: 30 },
    { wch: 18 },
    { wch: 15 },
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Relatório Curva ABC');

  const resumo = dados.resumo;
  const resumoData = [
    ['RESUMO DO RELATÓRIO'],
    [''],
    ['Período:', `${formatarData(dados.periodo.inicio)} até ${formatarData(dados.periodo.fim)}`],
    ['Tipo de Movimentação:', dados.tipo_movimentacao === 'entrada' ? 'Entrada' : 'Saída'],
    ['Total de Produtos:', resumo?.total_produtos ?? dados.dados.length],
    ['Total Movimentado:', dados.total_geral],
    [''],
    ['CLASSIFICAÇÃO ABC'],
    [''],
    ['Classe A:', `${resumo?.classificacao_a ?? 0} produtos (80% do valor)`],
    ['Classe B:', `${resumo?.classificacao_b ?? 0} produtos (15% do valor)`],
    ['Classe C:', `${resumo?.classificacao_c ?? 0} produtos (5% do valor)`],
  ];

  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  wsResumo['!cols'] = [{ wch: 25 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  const fileName = `relatorio_curva_abc_${filtros.tipoMovimentacao}_${filtros.dataInicio}_${filtros.dataFim}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

function RelatorioCurvaAbcPage() {
  const [filtros, setFiltros] = useState<IRelatorioCurvaAbcFiltros>(INITIAL_FILTROS);
  const [activeSearch, setActiveSearch] = useState<IRelatorioCurvaAbcSearch | null>(null);
  const [validationError, setValidationError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    data: reportData,
    isFetching: isGenerating,
    isError: isReportError,
    error: reportError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: activeSearch
      ? relatorioCurvaAbcKeys.report(activeSearch)
      : [...relatorioCurvaAbcKeys.all, 'report', 'idle'],
    queryFn: () => fetchCurvaAbcReport(activeSearch!),
    enabled: Boolean(activeSearch),
    retry: false,
  });

  const handleGenerate = () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setValidationError('Por favor, preencha as datas de início e fim.');
      setActiveSearch(null);
      return;
    }

    if (new Date(filtros.dataInicio) > new Date(filtros.dataFim)) {
      setValidationError('A data de início deve ser anterior à data de fim.');
      setActiveSearch(null);
      return;
    }

    setValidationError('');
    setCurrentPage(1);
    setActiveSearch({ ...filtros });
  };

  const errorMessage = (() => {
    if (validationError) return validationError;
    if (isReportError) {
      const message = reportError instanceof Error ? reportError.message : 'Erro ao gerar relatório';
      return message;
    }
    return '';
  })();

  const hasResults = Boolean(activeSearch && reportData && !isGenerating);

  const rows = reportData?.dados ?? EMPTY_ROWS;
  const totalProdutos = reportData?.resumo?.total_produtos ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const inicio = rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const fim = Math.min(currentPage * pageSize, rows.length);
  const paginationPages = getPaginationPages(currentPage, totalPages);

  const processadoEm = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const classCounts: Record<TRelatorioCurvaAbcClassificacao, number> = {
    A: reportData?.resumo?.classificacao_a ?? 0,
    B: reportData?.resumo?.classificacao_b ?? 0,
    C: reportData?.resumo?.classificacao_c ?? 0,
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <div className="relatorio-curva-abc-container">
      <div className="relatorio-curva-abc-content">
        <RelatorioCurvaAbcForm
          filtros={filtros}
          isGenerating={isGenerating}
          errorMessage={errorMessage}
          onFiltrosChange={setFiltros}
          onGenerate={handleGenerate}
        />

        {hasResults && reportData && (
          <>
            <Card className="relatorio-curva-abc-resumo-card">
              <Card.Header className="relatorio-curva-abc-resumo-header">
                <div className="relatorio-curva-abc-resumo-title">
                  <h3>Resumo da Movimentação</h3>
                  {processadoEm && (
                    <p>Dados processados em {processadoEm.replace(',', ' às')}</p>
                  )}
                </div>

                {rows.length > 0 && activeSearch && (
                  <button
                    type="button"
                    className="export-button"
                    onClick={() => exportarExcel(reportData, activeSearch)}
                  >
                    <FileDown
                      size={16}
                      aria-hidden="true"
                    />
                    Exportar Excel
                  </button>
                )}
              </Card.Header>

              <Card.Content>
                <div className="relatorio-curva-abc-resumo-grid">
                  <div className="relatorio-curva-abc-resumo-item">
                    <span className="relatorio-curva-abc-resumo-label">Período</span>
                    <span className="relatorio-curva-abc-resumo-value">
                      {formatarPeriodoCurto(reportData.periodo.inicio, reportData.periodo.fim)}
                    </span>
                  </div>
                  <div className="relatorio-curva-abc-resumo-item">
                    <span className="relatorio-curva-abc-resumo-label">Tipo</span>
                    <span className="relatorio-curva-abc-resumo-value">
                      {reportData.tipo_movimentacao === 'entrada' ? 'Entrada' : 'Saída'}
                    </span>
                  </div>
                  <div className="relatorio-curva-abc-resumo-item">
                    <span className="relatorio-curva-abc-resumo-label">Total de Itens</span>
                    <span className="relatorio-curva-abc-resumo-value">
                      {totalProdutos.toLocaleString('pt-BR')} SKUs
                    </span>
                  </div>
                  <div className="relatorio-curva-abc-resumo-item">
                    <span className="relatorio-curva-abc-resumo-label">Total Movimentado</span>
                    <span className="relatorio-curva-abc-resumo-value">
                      {formatarMoeda(reportData.total_geral)}
                    </span>
                  </div>
                </div>
              </Card.Content>
            </Card>

            {reportData.resumo && (
              <div className="relatorio-curva-abc-classificacao-grid">
                {(['A', 'B', 'C'] as TRelatorioCurvaAbcClassificacao[]).map((classe) => {
                  const meta = CLASS_META[classe];
                  const Icon = meta.Icon;
                  const count = classCounts[classe];
                  const estoquePct =
                    totalProdutos > 0 ? Math.round((count / totalProdutos) * 100) : 0;
                  const valorClasse = reportData.total_geral * (meta.valorPct / 100);

                  return (
                    <Card
                      key={classe}
                      className={`relatorio-curva-abc-class-card relatorio-curva-abc-class-card--${classe.toLowerCase()}`}
                    >
                      <Card.Content>
                        <div className="relatorio-curva-abc-class-card__top">
                          <span className="relatorio-curva-abc-class-badge">{meta.label}</span>
                          <Icon
                            size={20}
                            className="relatorio-curva-abc-class-icon"
                            aria-hidden="true"
                          />
                        </div>
                        <p className="relatorio-curva-abc-class-card__pct">
                          {meta.valorPct}% do valor movimentado
                        </p>
                        <p className="relatorio-curva-abc-class-card__value">
                          {formatarMoeda(valorClasse)}
                        </p>
                        <p className="relatorio-curva-abc-class-card__stock">
                          Corresponde a {estoquePct}% do estoque total
                        </p>
                      </Card.Content>
                    </Card>
                  );
                })}
              </div>
            )}

            {reportData.resumo && (
              <Card className="relatorio-curva-abc-chart-card">
                <Card.Header>
                  <h3>Distribuição por Classe</h3>
                </Card.Header>

                <Card.Content>
                  <div className="relatorio-curva-abc-chart-content">
                    <Bar
                      options={CLASS_CHART_OPTIONS}
                      data={buildClassChartData(classCounts)}
                      role="img"
                      aria-label={
                        'Gráfico de barras: quantidade de produtos por classificação ABC. ' +
                        `Classe A: ${classCounts.A}, Classe B: ${classCounts.B}, ` +
                        `Classe C: ${classCounts.C}.`
                      }
                    />
                  </div>

                  <div className="sr-only">
                    <table>
                      <caption>Quantidade de produtos por classificação ABC</caption>
                      <thead>
                        <tr>
                          <th scope="col">Classe</th>
                          <th scope="col">Produtos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(['A', 'B', 'C'] as TRelatorioCurvaAbcClassificacao[]).map((classe) => (
                          <tr key={classe}>
                            <th scope="row">{classe}</th>
                            <td>{classCounts[classe]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card.Content>
              </Card>
            )}

            <Card className="relatorio-curva-abc-table-card">
              <Card.Header>
                <h3>Dados Detalhados</h3>
              </Card.Header>

              <Card.Content>
                <div className="relatorio-curva-abc-table-container">
                  <Table>
                    <caption className="sr-only">
                      Dados detalhados da Curva ABC, posição {inicio} a {fim} de {rows.length}{' '}
                      itens
                    </caption>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head scope="col">Pos.</Table.Head>
                        <Table.Head scope="col">Código</Table.Head>
                        <Table.Head scope="col">Descrição</Table.Head>
                        <Table.Head scope="col">
                          {reportData.tipo_movimentacao === 'entrada'
                            ? 'Entrada/Moviment.'
                            : 'Venda/Moviment.'}
                        </Table.Head>
                        <Table.Head scope="col">Saldo Estoque</Table.Head>
                        <Table.Head scope="col">%</Table.Head>
                        <Table.Head scope="col">% Acum.</Table.Head>
                        <Table.Head scope="col">Classe</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Content>
                      {paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => {
                          const absoluteIndex = (currentPage - 1) * pageSize + index;
                          return (
                            <Table.Row key={`${item.codigo_produto}-${absoluteIndex}`}>
                              <Table.Cell className="relatorio-curva-abc-pos">
                                {formatarPosicao(absoluteIndex)}
                              </Table.Cell>
                              <Table.Cell>
                                <span className="relatorio-curva-abc-codigo">
                                  {item.codigo_produto}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="relatorio-curva-abc-descricao">
                                {item.descricao}
                              </Table.Cell>
                              <Table.Cell className="relatorio-curva-abc-numero">
                                {formatarMoeda(item.movimentacao_periodo)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-curva-abc-saldo">
                                {item.saldo_estoque.toLocaleString('pt-BR')} un
                              </Table.Cell>
                              <Table.Cell className="relatorio-curva-abc-numero">
                                {formatarPercentual(item.percentual)}
                              </Table.Cell>
                              <Table.Cell className="relatorio-curva-abc-numero">
                                {formatarPercentual(item.percentual_acumulado)}
                              </Table.Cell>
                              <Table.Cell>
                                <span
                                  className={`relatorio-curva-abc-classificacao-badge relatorio-curva-abc-classificacao-badge--${item.classificacao.toLowerCase()}`}
                                >
                                  {item.classificacao}
                                </span>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      ) : (
                        <Table.Row>
                          <Table.Cell
                            colSpan={8}
                            className="relatorio-curva-abc-no-data"
                          >
                            Nenhum dado encontrado para o período selecionado.
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Content>
                  </Table>
                </div>
              </Card.Content>

              {rows.length > 0 && (
                <Card.Footer className="relatorio-curva-abc-pagination">
                  <div className="relatorio-curva-abc-pagination__left">
                    <label htmlFor="curva-abc-page-size">Itens por página:</label>
                    <select
                      id="curva-abc-page-size"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option
                          key={size}
                          value={size}
                        >
                          {size}
                        </option>
                      ))}
                    </select>
                    <p>
                      Mostrando{' '}
                      <strong>
                        {inicio.toLocaleString('pt-BR')} - {fim.toLocaleString('pt-BR')}
                      </strong>{' '}
                      de <strong>{rows.length.toLocaleString('pt-BR')}</strong> itens
                    </p>
                  </div>

                  <div className="relatorio-curva-abc-pagination__controls">
                    <button
                      type="button"
                      className="relatorio-curva-abc-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      aria-label="Página anterior"
                    >
                      ←
                    </button>

                    {paginationPages.map((page, index) =>
                      page === 'ellipsis' ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="relatorio-curva-abc-page-ellipsis"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          type="button"
                          className={[
                            'relatorio-curva-abc-page-btn',
                            page === currentPage ? 'relatorio-curva-abc-page-btn--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ),
                    )}

                    <button
                      type="button"
                      className="relatorio-curva-abc-page-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      aria-label="Próxima página"
                    >
                      →
                    </button>
                  </div>
                </Card.Footer>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
